<template>
  <div class="grading-item">
    <div class="label-description-container section">
      <label class="title">{{ label }}</label>
      <p class="description">{{ description }}</p>
    </div>
    <div class="grading-container section">
      <label class="title">Effect Rating</label>
      <radio-buttons :value="grade" :options="gradeOptions" :bordered="true" :disabled="readonly" @input="updateGrade" />
    </div>
    <div class="points-container section">
      <label class="title">Points</label>
      <div class="points-slider-container">
        <span>0&nbsp;&nbsp;</span>
        <vue-slider
          class="points-slider"
          :min="0"
          :max="5"
          :interval="1"
          :adsorb="true"
          :lazy="true"
          :disabled="readonly || grade === 'NEUTRAL'"
          :rail-style="{backgroundColor: '#C1DAEB'}"
          :process-style="{backgroundColor: '#40883F'}"
          :dot-style="{backgroundColor: '#40883F'}"
          :dot-options="{focusStyle: {boxShadow: '0 0 8px 0', color: '#40883F'}}"
          tooltip="none"
          :dot-size="12"
          :height="2"
          :stop-propagation="true"
          :value="points"
          @change="updatePoints"
          @drag-start="onPointsDragStart"
          @drag-end="onPointsDragEnd"
        />
        <span>&nbsp;&nbsp;5</span>
      </div>
      <span>{{ points }}</span>
    </div>
    <div class="critical-container section">
      <label class="title">Critical Action</label>
      <div>
        <md-checkbox :value="isCritical" :disabled="readonly || grade === 'NEUTRAL'" @change="updateIsCritical" />
        <md-tooltip md-direction="left" md-delay="300">
          Critical actions will cause the
          <br>user to fail the case if missed.
        </md-tooltip>
      </div>
    </div>
  </div>
</template>

<script>
import VueSlider from 'vue-slider-component';

import RadioButtons from '~lib/common/components/RadioButtons';

export default {
  name: 'grading-item',
  components: {
    RadioButtons,
    VueSlider,
  },
  props: {
    label: {
      type: String,
      required: true,
    },
    description: {
      type: String,
      default: '',
    },
    grade: {
      type: String,
      validator: value => ['GOOD', 'BAD', 'NEUTRAL'].includes(value),
      default: 'GOOD',
    },
    points: {
      type: Number,
      default: 0,
    },
    isCritical: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    disableNeutral: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      gradeOptions: [
        {
          value: 'GOOD',
          label: 'NEED',
          activeColor: 'green-3',
          inactiveColor: 'white',
          tooltip: 'A positive action that the user should do during the case',
        },
        !this.disableNeutral && {
          value: 'NEUTRAL',
          label: 'NO EFFECT',
          activeColor: 'gray-5',
          inactiveColor: 'white',
          tooltip: 'An action that has neither a positive or negative connotation within the case',
        },
        {
          value: 'BAD',
          label: 'AVOID',
          activeColor: 'red-3',
          inactiveColor: 'white',
          tooltip: 'A negative action that the user should not do during the case',
        },
      ].filter(v => !!v),
    };
  },
  methods: {
    updateGrade(value) {
      this.$emit('changeGrade', value);
      if (value === 'NEUTRAL') {
        this.$emit('changeIsCritical', false);
        this.$emit('changePoints', 0);
      }
    },
    updatePoints(value) {
      this.$emit('changePoints', value);
      if (value === 0) {
        this.$emit('changeIsCritical', false);
      }
    },
    updateIsCritical(value) {
      this.$emit('changeIsCritical', value);
    },
    onPointsDragStart(...args) {
      this.$emit('pointsDragStart', ...args);
    },
    onPointsDragEnd(...args) {
      this.$emit('pointsDragEnd', ...args);
    },
  },
};
</script>

<style scoped>
.grading-item {
  padding: 7px 12px;
  display: flex;
  border-bottom: solid 1px #cccccc;
}

.section {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 0px 2px;
}

.label-description-container {
  flex: 2;
  align-items: flex-start;
}

.grading-container {
  flex: 1.6;
  min-width: 280px;
}

.points-container, .critical-container {
  flex: 0.8;
  min-width: 140px;
}
.critical-container .md-checkbox {
  margin: 12px 0;
}

.description {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1; /* number of lines to show */
  margin: 0;
  font-weight: 500;
}

.title {
  text-transform: uppercase;
  margin: 5px 0;
}

.points-slider-container {
  align-self: stretch;
  display: flex;
  align-items: center;
}

.points-slider {
  flex: 1;
}
</style>